import { ArrowLeftIcon } from '@phosphor-icons/react'
import { type ElementType, type ReactElement, useEffect, useMemo, useState } from 'react'
import { CustomPageForm } from 'app/components/contacts/CustomPageForm'
import { ListVisibilityField } from 'app/components/lists/ListVisibilityField'
import { AddPipeline } from 'app/components/pipelines/AddPipeline'
import { Button } from 'app/components/ui/button'
import { setModalTitle } from 'app/lib/modal'
import { cn } from 'app/lib/utils'
import type { CustomPages } from 'app/types/entities'
import type { ListKind } from 'app/types/list-kind'
import type { ListVisibility } from 'app/types/list-visibility'
import { LIST_KINDS } from 'lib/listKinds'
import { LIST_TEMPLATES, type ListTemplate, type ListTemplateRecordType } from 'lib/listTemplates'
import { RECORD_TYPES } from 'lib/recordTypes'
import { useTemplateCounts } from './useTemplateCounts'

export type NewListWizardProps = {
  recordTypes: string[]
  canCreateManualList: boolean
  createManualList: (data: { [k: string]: FormDataEntryValue }, visibility: ListVisibility) => Promise<void>
  onListsChanged: () => void
}

type Step =
  | { kind: `gallery` }
  | { kind: `pickRecordType` }
  | { kind: `filter`; recordType: CustomPages; template?: ListTemplate }
  | { kind: `manual` }

export const WIZARD_TITLE = `New list`

// The card variant's own look, stacked: a title row over its blurb, flat rather than lifted.
const CARD_CLASS = `flex-col items-start gap-1 rounded-lg font-normal shadow-none hover:shadow-none`

function GalleryCard({
  title,
  blurb,
  meta,
  icon,
  onPick,
  ariaLabel,
  blank,
}: {
  title: string
  blurb: string
  meta?: string
  // `RECORD_TYPES[...].icon` is a component (`ElementType`), rendered as `<Icon />`, the
  // same pattern `DraggableListRow` and `ListsSection` use elsewhere for it.
  icon?: ElementType
  onPick: () => void
  // The card's own text runs the title into the blurb as one accessible name (e.g. "Activities A
  // filter list of activities."). The record type picker needs a name that is just the record
  // type, so it can pass one explicitly instead of letting the blurb bleed into it.
  ariaLabel?: string
  /**
   * A blank start draws dashed, so it never reads as one more template, with its kind's icon:
   * the mark that list carries beside its title once it exists.
   */
  blank?: ListKind
}): ReactElement {
  const Icon = icon
  const BlankIcon = blank && LIST_KINDS[blank].Icon
  return (
    <Button
      type="button"
      variant="card"
      className={cn(CARD_CLASS, blank && `border-dashed bg-transparent`)}
      onClick={onPick}
      aria-label={ariaLabel}
    >
      <span className="flex w-full items-center gap-2">
        {BlankIcon && (
          <span className="text-subtle">
            <BlankIcon size={16} weight="bold" />
          </span>
        )}
        {Icon && (
          <span className="text-subtle">
            <Icon />
          </span>
        )}
        <span className="text-ui-sm text-strong">{title}</span>
        {meta && <span className="ml-auto text-body-xs text-subtle">{meta}</span>}
      </span>
      <span className="text-body-xs text-subtle">{blurb}</span>
    </Button>
  )
}

function Group({ title, children }: { title: string; children: ReactElement[] }): ReactElement | null {
  if (children.length === 0) return null
  return (
    <section className="flex flex-col gap-2">
      <p className="text-label-xs uppercase text-subtle">{title}</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">{children}</div>
    </section>
  )
}

/**
 * A step's own padding: 16px on a phone, where every pixel of the template tiles counts, and
 * the dialog's usual 24px from sm up. The filter step's form pads itself.
 */
const STEP_PADDING = `px-4 py-4 sm:px-6`

export function NewListWizard({
  recordTypes,
  canCreateManualList,
  createManualList,
  onListsChanged,
}: NewListWizardProps): ReactElement {
  const [step, setStep] = useState<Step>({ kind: `gallery` })
  const [visibility, setVisibility] = useState<ListVisibility>(`shared`)
  // Memoized so `useTemplateCounts` sees a stable array: a fresh `.filter()` result on every
  // render would re-run its fetch effect (its dependency is `[templates]`) each time this
  // component re-renders, including the re-renders the fetch's own state update causes.
  const offered = useMemo(
    () => LIST_TEMPLATES.filter((template) => recordTypes.includes(template.recordType)),
    [recordTypes],
  )
  const counts = useTemplateCounts(offered)

  useEffect(() => {
    if (step.kind === `gallery` || step.kind === `pickRecordType`) setModalTitle(WIZARD_TITLE)
    else if (step.kind === `manual`) setModalTitle(`New manual list`)
    else setModalTitle(step.template?.name ?? `New filter list`)
  }, [step])

  const back = (
    <Button variant="ghost" size="sm" className="self-start" onClick={() => setStep({ kind: `gallery` })}>
      <ArrowLeftIcon size={14} weight="bold" /> Back
    </Button>
  )

  if (step.kind === `filter`) {
    // Back returns to the step that led here: the record type choice for a blank list when there
    // was a choice to make, the gallery for a template or when there was not.
    const backTo: Step = step.template || recordTypes.length === 1 ? { kind: `gallery` } : { kind: `pickRecordType` }
    return (
      <CustomPageForm
        page={step.recordType}
        // A contacts filter list is a List; the other record types keep saving custom pages.
        saveTo="list"
        initialName={step.template?.name}
        initialFilters={step.template?.filters}
        onPageCreated={onListsChanged}
        onBack={() => setStep(backTo)}
      />
    )
  }

  if (step.kind === `manual`) {
    return (
      <div className={cn(`flex flex-col gap-3`, STEP_PADDING)}>
        {back}
        <AddPipeline createPipeline={(data) => createManualList(data, visibility)}>
          <ListVisibilityField label="Who can see it" value={visibility} onChange={setVisibility} />
        </AddPipeline>
      </div>
    )
  }

  const startBlankFilter = () => {
    if (recordTypes.length === 1) setStep({ kind: `filter`, recordType: recordTypes[0] as CustomPages })
    else setStep({ kind: `pickRecordType` })
  }

  if (step.kind === `pickRecordType`) {
    return (
      <div className={cn(`flex flex-col gap-3`, STEP_PADDING)}>
        {back}
        <p className="text-body-sm text-subtle">What should this list hold?</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {recordTypes.map((recordType) => (
            <GalleryCard
              key={recordType}
              title={RECORD_TYPES[recordType as CustomPages].label}
              blurb={`A filter list of ${RECORD_TYPES[recordType as CustomPages].label.toLowerCase()}.`}
              icon={RECORD_TYPES[recordType as CustomPages].icon}
              onPick={() => setStep({ kind: `filter`, recordType: recordType as CustomPages })}
              ariaLabel={RECORD_TYPES[recordType as CustomPages].label}
            />
          ))}
        </div>
      </div>
    )
  }

  const templateCards = (recordType: ListTemplateRecordType) =>
    offered
      .filter((template) => template.recordType === recordType)
      .map((template) => (
        <GalleryCard
          key={template.id}
          title={template.name}
          blurb={template.blurb}
          meta={counts[template.id] === undefined ? undefined : `${counts[template.id]!.toLocaleString(`en-US`)} today`}
          icon={RECORD_TYPES[recordType].icon}
          onPick={() => setStep({ kind: `filter`, recordType, template })}
          ariaLabel={template.name}
        />
      ))

  const hasTemplates = offered.length > 0

  return (
    <div className={cn(`flex flex-col gap-6`, STEP_PADDING)}>
      <Group title="Start from scratch">
        {[
          <GalleryCard
            key="filter"
            blank="filter"
            title="Blank filter list"
            blurb={LIST_KINDS.filter.blurb}
            onPick={startBlankFilter}
          />,
          ...(canCreateManualList
            ? [
                <GalleryCard
                  key="manual"
                  blank="manual"
                  title="Blank manual list"
                  blurb={LIST_KINDS.manual.blurb}
                  onPick={() => setStep({ kind: `manual` })}
                />,
              ]
            : []),
        ]}
      </Group>
      {hasTemplates && (
        <section className="flex flex-col gap-4 border-t border-default pt-5">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-ui-sm text-strong">Templates</h3>
            <p className="text-body-xs text-subtle">Ready-made lists you can save as your own and change later.</p>
          </div>
          <Group title={RECORD_TYPES.contacts.label}>{templateCards(`contacts`)}</Group>
          <Group title={RECORD_TYPES.activities.label}>{templateCards(`activities`)}</Group>
        </section>
      )}
    </div>
  )
}
